import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Mail,
  Phone,
  Building2,
  User,
  Calendar,
  Eye,
  CheckCircle,
  MessageSquare,
  Loader2,
  RefreshCw,
  Lock,
  LogOut,
  Inbox,
  Clock,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple password protection (in production, use proper auth)
const ADMIN_PASSWORD = 'NoraGold2024!';

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    const authStatus = sessionStorage.getItem('adminAuth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch submissions when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchSubmissions();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
    setPassword('');
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contact`);
      // Sort by date, newest first
      const sorted = response.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setSubmissions(sorted);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
    setLoading(false);
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/contact/${id}`, { status: newStatus });
      setSubmissions((prev) =>
        prev.map((sub) =>
          sub.id === id ? { ...sub, status: newStatus } : sub
        )
      );
      if (selectedSubmission?.id === id) {
        setSelectedSubmission((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const openModal = (submission) => {
    setSelectedSubmission(submission);
    setIsModalOpen(true);
    // Mark as read if new
    if (submission.status === 'new') {
      updateStatus(submission.id, 'read');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700 border-blue-200',
      read: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      replied: 'bg-green-100 text-green-700 border-green-200',
    };
    const icons = {
      new: <Inbox size={12} />,
      read: <Eye size={12} />,
      replied: <CheckCircle size={12} />,
    };
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border rounded-full ${styles[status]}`}
      >
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-burgundy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="text-burgundy" size={32} />
              </div>
              <h1 className="text-2xl font-bold text-charcoal">Admin Access</h1>
              <p className="text-slate mt-2">Enter password to view submissions</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="w-full"
                />
              </div>
              {passwordError && (
                <p className="text-red-500 text-sm">{passwordError}</p>
              )}
              <Button
                type="submit"
                className="w-full bg-burgundy hover:bg-burgundy/90 text-white"
              >
                <Lock size={16} className="mr-2" />
                Access Dashboard
              </Button>
            </form>

            <div className="mt-6 text-center">
              <a
                href="/"
                className="text-burgundy hover:text-gold text-sm inline-flex items-center gap-1"
              >
                <ArrowLeft size={14} />
                Back to Website
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-charcoal text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Nora Gold Advisory" className="h-10" />
            <div>
              <h1 className="text-xl font-bold">Admin Dashboard</h1>
              <p className="text-ivory/60 text-sm">Contact Form Submissions</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={fetchSubmissions}
              variant="outline"
              size="sm"
              className="border-ivory/30 text-ivory hover:bg-ivory/10"
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              <span className="ml-2 hidden sm:inline">Refresh</span>
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-ivory/30 text-ivory hover:bg-ivory/10"
            >
              <LogOut size={16} />
              <span className="ml-2 hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Inbox className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-slate text-sm">New Messages</p>
                <p className="text-2xl font-bold text-charcoal">
                  {submissions.filter((s) => s.status === 'new').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Eye className="text-yellow-600" size={24} />
              </div>
              <div>
                <p className="text-slate text-sm">Read</p>
                <p className="text-2xl font-bold text-charcoal">
                  {submissions.filter((s) => s.status === 'read').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-slate text-sm">Replied</p>
                <p className="text-2xl font-bold text-charcoal">
                  {submissions.filter((s) => s.status === 'replied').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-bold text-charcoal flex items-center gap-2">
              <MessageSquare size={20} />
              All Submissions ({submissions.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="animate-spin mx-auto text-burgundy" size={40} />
              <p className="text-slate mt-4">Loading submissions...</p>
            </div>
          ) : submissions.length === 0 ? (
            <div className="p-12 text-center">
              <Inbox className="mx-auto text-gray-300" size={48} />
              <p className="text-slate mt-4">No submissions yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {submissions.map((submission) => (
                    <tr
                      key={submission.id}
                      className={`hover:bg-gray-50 transition-colors ${
                        submission.status === 'new' ? 'bg-blue-50/30' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(submission.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-slate" />
                          <span className="font-medium text-charcoal">
                            {submission.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <a
                          href={`mailto:${submission.email}`}
                          className="text-burgundy hover:text-gold flex items-center gap-2"
                        >
                          <Mail size={14} />
                          {submission.email}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-slate">
                        {submission.company || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-slate text-sm">
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          {formatDate(submission.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Button
                          onClick={() => openModal(submission)}
                          size="sm"
                          className="bg-burgundy hover:bg-burgundy/90 text-white"
                        >
                          <Eye size={14} className="mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Message Detail Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="text-burgundy" size={20} />
              Message Details
            </DialogTitle>
          </DialogHeader>

          {selectedSubmission && (
            <div className="space-y-6">
              {/* Sender Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-slate uppercase">Name</p>
                  <p className="font-medium text-charcoal flex items-center gap-2">
                    <User size={16} className="text-burgundy" />
                    {selectedSubmission.name}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate uppercase">Email</p>
                  <a
                    href={`mailto:${selectedSubmission.email}`}
                    className="font-medium text-burgundy hover:text-gold flex items-center gap-2"
                  >
                    <Mail size={16} />
                    {selectedSubmission.email}
                  </a>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate uppercase">Company</p>
                  <p className="font-medium text-charcoal flex items-center gap-2">
                    <Building2 size={16} className="text-burgundy" />
                    {selectedSubmission.company || 'Not provided'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate uppercase">Phone</p>
                  <p className="font-medium text-charcoal flex items-center gap-2">
                    <Phone size={16} className="text-burgundy" />
                    {selectedSubmission.phone || 'Not provided'}
                  </p>
                </div>
              </div>

              {/* Date & Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-slate">
                  <Calendar size={16} />
                  {formatDate(selectedSubmission.created_at)}
                </div>
                {getStatusBadge(selectedSubmission.status)}
              </div>

              {/* Message */}
              <div className="space-y-2">
                <p className="text-xs text-slate uppercase">Message</p>
                <div className="p-4 bg-ivory rounded-lg border border-gray-200">
                  <p className="text-charcoal whitespace-pre-wrap">
                    {selectedSubmission.message}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t">
                <Button
                  onClick={() => updateStatus(selectedSubmission.id, 'replied')}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={selectedSubmission.status === 'replied'}
                >
                  <CheckCircle size={16} className="mr-2" />
                  Mark as Replied
                </Button>
                <a
                  href={`mailto:${selectedSubmission.email}?subject=Re: Your inquiry to Nora Gold Advisory`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-burgundy hover:bg-burgundy/90 text-white rounded-md transition-colors"
                >
                  <Mail size={16} />
                  Reply via Email
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
